import type { PageProps } from 'gatsby';
import React from 'react';
import { useIntl } from 'react-intl';

import NotFound from '@components/NotFound';

import { ScanResultProvider } from '@contexts/index';

import type { ImageType } from '@models/image-type';

import ActivityMain from '@views/Records/Activity/Main';
import AllergiesMain from '@views/Records/Allergies/Main';
import AppointmentsMain from '@views/Records/Appointments/Main';
import BodyMain from '@views/Records/Body/Main';
import MainWrapper from '@views/Records/Common/MainWrapper';
import EmergencyVisitsMain from '@views/Records/EmergencyVisits/Main';
import FamilyHistoryMain from '@views/Records/FamilyHistory/Main';
import FitnessMain from '@views/Records/Fitness/Main';
import GeneticInformationMain from '@views/Records/GeneticInformation/AuthMain';
import HospitalizationsMain from '@views/Records/Hospitalizations/Main';
import ImagingTestsMain from '@views/Records/ImagingTests/Main';
import ImmunizationsMain from '@views/Records/Immunizations/Main';
import LabResultsMain from '@views/Records/LabResults/AuthMain';
import MedicalConditionsMain from '@views/Records/MedicalConditions/Main';
import MedicationsMain from '@views/Records/Medications/Main';
import ScanHistoryMain from '@views/Records/Scan/AuthMain';
import SleepMain from '@views/Records/Sleep/Main';
import SurgeriesMain from '@views/Records/Surgeries/Main';
import VitalSignsMain from '@views/Records/VitalSigns/Main';

const RECORD_TYPE_MAP: Record<string, { icon: ImageType; title: string }> = {
  activity: {
    icon: 'activity',
    title: 'activityMain.title',
  },
  allergies: { icon: 'allergies', title: 'allergiesMain.allergies' },
  appointments: { icon: 'appointments', title: 'appointmentsMain.title' },
  body: { icon: 'body', title: 'bodyMain.title' },
  'body-scan-history': {
    icon: 'logo',
    title: 'bodyScansMain.title',
  },
  'cough-scan-history': {
    icon: 'logo',
    title: 'coughScansMain.title',
  },
  'daily-stats': {
    icon: 'fitness',
    title: 'fitnessMain.title',
  },
  'emergency-visits': {
    icon: 'emergencyVisits',
    title: 'emergencyVisitsMain.title',
  },
  'face-scan-history': {
    icon: 'logo',
    title: 'faceScansMain.title',
  },
  'family-history': { icon: 'familyHistory', title: 'familyHistoryMain.title' },
  'genetic-information': {
    icon: 'geneticInformation',
    title: 'geneticInformationPage.title',
  },
  hospitalizations: {
    icon: 'hospitalizations',
    title: 'hospitalizationsMain.title',
  },
  'imaging-tests': { icon: 'imagingTests', title: 'imagingTests.title' },
  immunizations: { icon: 'immunizations', title: 'immunizationsPage.title' },
  'lab-results': { icon: 'labResults', title: 'labResultsMain.title' },
  'medical-conditions': {
    icon: 'medicalConditions',
    title: 'medicalConditionsMain.title',
  },
  medications: { icon: 'medications', title: 'medicationsMain.title' },
  'skin-scan-history': { icon: 'logo', title: 'skinScansMain.title' },
  sleep: { icon: 'sleep', title: 'sleepMain.title' },
  surgeries: { icon: 'surgeries', title: 'surgeriesMain.title' },
  'vital-signs': { icon: 'vitalSigns', title: 'vitalSignsMain.title' },
};

export default function RecordsMainPage({ params }: PageProps) {
  const { formatMessage } = useIntl();

  const { icon = 'logo', title = '' } =
    params.type in RECORD_TYPE_MAP ? RECORD_TYPE_MAP[params.type] : {};

  const getRecordsList = () => {
    switch (params.type) {
      case 'activity':
        return <ActivityMain {...params} />;
      case 'allergies':
        return <AllergiesMain {...params} />;
      case 'appointments':
        return <AppointmentsMain {...params} />;
      case 'body':
        return <BodyMain {...params} />;
      case 'body-scan-history':
      case 'cough-scan-history':
      case 'face-scan-history':
      case 'skin-scan-history':
        return (
          <ScanResultProvider scanType={params.type}>
            <ScanHistoryMain {...params} />
          </ScanResultProvider>
        );
      case 'daily-stats':
        return <FitnessMain {...params} />;
      case 'emergency-visits':
        return <EmergencyVisitsMain {...params} />;
      case 'family-history':
        return <FamilyHistoryMain {...params} />;
      case 'genetic-information':
        return <GeneticInformationMain {...params} />;
      case 'hospitalizations':
        return <HospitalizationsMain {...params} />;
      case 'imaging-tests':
        return <ImagingTestsMain {...params} />;
      case 'immunizations':
        return <ImmunizationsMain {...params} />;
      case 'lab-results':
        return <LabResultsMain {...params} />;
      case 'medical-conditions':
        return <MedicalConditionsMain {...params} />;
      case 'medications':
        return <MedicationsMain {...params} />;
      case 'sleep':
        return <SleepMain {...params} />;
      case 'surgeries':
        return <SurgeriesMain {...params} />;
      case 'vital-signs':
        return <VitalSignsMain {...params} />;
      default:
        return <NotFound />;
    }
  };

  return (
    <MainWrapper
      backgroundIcon={icon}
      friendlyName={title ? formatMessage({ id: title }) : ''}
      title={title ? formatMessage({ id: title }) : ''}
    >
      {getRecordsList()}
    </MainWrapper>
  );
}
